import { useState, FC, useRef, useEffect, useCallback, useMemo } from 'react';
import { Icon, tailwindOverride, Tooltip } from '@getsynapse/design-system';
import { EditableTableRowCell } from '../EditableTableRowCell';
import { BaseCellProps } from '../types';
import { Option, PilotPagesToTrack } from 'utils/customTypes';
import intl from 'react-intl-universal';
import { TaskAssignee } from 'types/store/tasks';
import AssigneesColumn from 'Pages/TasksListPage/components/v2/TasksListTable/TaskRow/AssigneesColumn/AssigneesColumn';
import { AssigneesDropdown } from './AssigneesDropdown';
import { convertToOptions } from './utils';
import { getTooltipMessage } from '../utils';
import { useTooltip } from '../hooks';
import { Userpilot } from 'userpilot';
import { USERPILOT_EVENTS } from 'utils/constants';

export type StatusOption = Option & { disabled?: boolean; message?: string };

interface AssigneeTableRowCellProps extends Omit<BaseCellProps, 'value'> {
  currentAssignees: TaskAssignee[];
  availableAssignees: TaskAssignee[];
  placeholder?: string;
  isTaskProjectDisabled?: boolean;
  canInlineEditTask?: boolean;
  hasPermission?: boolean;
  setIsTimeToFetchData: (isOpen: boolean) => void;
  onSearchChange?: (searchTerm: string) => void;
  isFetchingAvailableAssignees: boolean;
  userPilotEventOrigin?:
    | PilotPagesToTrack.CENTRALIZED_TASK
    | PilotPagesToTrack.PROJECT_TASK
    | PilotPagesToTrack.UNKNOWN;
}

export const AssigneeTableRowCell: FC<AssigneeTableRowCellProps> = ({
  currentAssignees,
  availableAssignees,
  isEditable,
  className = '',
  onSave,
  onCancel,
  isTaskProjectDisabled,
  canInlineEditTask,
  hasPermission,
  setIsTimeToFetchData,
  onSearchChange,
  isFetchingAvailableAssignees,
  userPilotEventOrigin,
}) => {
  const saveTriggerRef = useRef<(() => void) | null>(null);
  const cellRef = useRef<HTMLDivElement | null>(null);
  const dropdownContainerRef = useRef<HTMLDivElement | null>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [assigneesHistory, setAssigneesHistory] =
    useState<TaskAssignee[]>(currentAssignees);

  const { tooltipRef, closeTooltip } = useTooltip();

  useEffect(() => {
    setAssigneesHistory((prevAssignees) => {
      const assigneesMap = new Map(prevAssignees.map((a) => [a.id, a]));

      currentAssignees.forEach((assignee) => {
        assigneesMap.set(assignee.id, assignee);
      });

      return Array.from(assigneesMap.values());
    });
  }, [currentAssignees]);

  const assigneesOptions = useMemo(() => {
    const currentAssigneeIds = new Set(
      currentAssignees.map((assignee) => assignee.id)
    );

    const assigneesMap = new Map<string, TaskAssignee>();

    assigneesHistory.forEach((assignee) => {
      assigneesMap.set(assignee.id, assignee);
    });

    availableAssignees.forEach((assignee) => {
      assigneesMap.set(assignee.id, assignee);
    });

    const filteredAssignees = Array.from(assigneesMap.values()).filter(
      (assignee) =>
        !searchTerm ||
        assignee.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filteredAssignees.map((assignee) =>
      convertToOptions(assignee, currentAssigneeIds.has(assignee.id))
    );
  }, [availableAssignees, assigneesHistory, currentAssignees, searchTerm]);

  const hoverStateStyles = tailwindOverride(
    'bg-white border border-transparent hover:border-cool-mist group-focus-visible:border-cool-mist rounded flex items-center justify-between p-2 ml-1'
  );

  const fieldChildren = (
    <>
      <AssigneesColumn
        assignees={currentAssignees}
        unassignedText={intl.get('TASKS.TABLE.UNASSIGNED')}
      />
      <Icon name='caret-down-outline' />
    </>
  );

  const getHoverField = () => {
    if (!canInlineEditTask || isTaskProjectDisabled || !hasPermission) {
      return (
        <div
          className={tailwindOverride(
            'cursor-auto',
            hoverStateStyles,
            'border-cool-mist bg-off-white'
          )}
          aria-disabled={true}
        >
          {fieldChildren}
        </div>
      );
    }

    return (
      <div
        className={tailwindOverride(
          'cursor-pointer',
          hoverStateStyles,
          'border border-neutral-black rounded'
        )}
      >
        {fieldChildren}
      </div>
    );
  };

  const hoverInputChildren = getHoverField();

  const handleEditModeClick = () => {
    if (!isDropdownOpen) return;

    saveTriggerRef.current?.();
  };

  const editModeChildren = (
    <div className='w-full' onClick={handleEditModeClick}>
      {hoverInputChildren}
    </div>
  );

  const readModeChildren = (
    <div className='px-4 py-2'>
      <AssigneesColumn
        assignees={currentAssignees}
        unassignedText={intl.get('TASKS.TABLE.UNASSIGNED')}
      />
    </div>
  );

  const handleCancel = () => {
    setIsDropdownOpen(false);
    onCancel?.();
  };

  const onChange = (userId: string) => {
    onSave?.(userId);

    try {
      Userpilot.track(USERPILOT_EVENTS.TASK_ASSIGNEE_INLINE_EDITING, {
        eventOrigin: userPilotEventOrigin,
        updatedValue: userId,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Error tracking Userpilot event for edit task assignees',
        error
      );
    }
  };

  const tooltipMessage = getTooltipMessage(
    isTaskProjectDisabled,
    canInlineEditTask,
    hasPermission
  );

  const handleEnterEditMode = useCallback(() => {
    closeTooltip();
    setIsDropdownOpen(true);
    setIsTimeToFetchData(true);
  }, [closeTooltip, setIsTimeToFetchData]);

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
    setIsTimeToFetchData(false);
    setSearchTerm('');
  }, [setIsDropdownOpen, setIsTimeToFetchData, setSearchTerm]);

  const handleSearchChange = useCallback(
    (search: string) => {
      setSearchTerm(search);
      onSearchChange?.(search);
    },
    [onSearchChange]
  );

  useEffect(() => {
    if (!isDropdownOpen) return;

    const preventDefault = (e: Event) => e.preventDefault();

    const preventDefaultForScrollKeys = (e: KeyboardEvent) => {
      const scrollKeys = [
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'PageUp',
        'PageDown',
        'Home',
        'End',
        ' ',
      ];

      if (scrollKeys.includes(e.key)) {
        e.preventDefault();
      }

      if (e.key === 'Escape') {
        const dropdownEl = dropdownContainerRef.current;
        const target = e.target as Node | null;
        if (dropdownEl && target && dropdownEl.contains(target)) {
          e.stopPropagation();
          closeDropdown();
        }
      }
    };

    const handleWheel = (event: WheelEvent) => {
      const dropdownEl = dropdownContainerRef.current;
      if (!dropdownEl) return;

      const target = event.target as Node | null;
      if (!target || !dropdownEl.contains(target)) {
        event.preventDefault();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchmove', preventDefault, { passive: false });
    window.addEventListener('keydown', preventDefaultForScrollKeys, {
      passive: false,
    });

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', preventDefault);
      window.removeEventListener('keydown', preventDefaultForScrollKeys);
    };
  }, [isDropdownOpen, closeDropdown]);

  return (
    <div ref={cellRef}>
      <Tooltip
        ref={tooltipRef}
        openMode='hover1'
        timeout={0}
        showPopper
        position='topCenter'
        contentProps={{
          className: 'px-3 py-2 text-sm z-50 absolute',
        }}
        className='w-full'
        usePortal
        trigger={
          <div className='w-full'>
            <EditableTableRowCell
              isEditable={!!isEditable}
              editModeChildren={editModeChildren}
              readModeChildren={readModeChildren}
              hoverInputChildren={hoverInputChildren}
              showHoverInput={true}
              className={className}
              onSave={closeDropdown}
              onCancel={handleCancel}
              saveTriggerRef={saveTriggerRef}
              closeTooltip={closeTooltip}
              onEnterEditMode={handleEnterEditMode}
              externalInteractiveRef={dropdownContainerRef}
            />
          </div>
        }
      >
        {tooltipMessage}
      </Tooltip>

      <AssigneesDropdown
        assignees={assigneesOptions}
        onChange={onChange}
        onSearchChange={handleSearchChange}
        isDropdownOpen={isDropdownOpen}
        cellRef={cellRef}
        dropdownContainerRef={dropdownContainerRef}
        isFetchingAvailableAssignees={isFetchingAvailableAssignees}
      />
    </div>
  );
};
